import { ownerKeyOf } from "@/api/session";
import { type VaultStatus, forgetSecret, vaultStatus } from "@/unlock";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useSession } from "./session";

const ARM_KEY = "maqia.crm.pin.offer";
const DECLINED_PREFIX = "maqia.crm.pin.declined:";

const EMPTY_STATUS: VaultStatus = { saved: false, label: null, attemptsLeft: 0 };

function vaultQueryKey(ownerKey: string): readonly string[] {
  return ["unlock", "vault", ownerKey];
}

export function armPinOffer(): void {
  localStorage.setItem(ARM_KEY, "armed");
}

export function pinOfferArmed(ownerKey: string): boolean {
  return (
    localStorage.getItem(ARM_KEY) === "armed" &&
    localStorage.getItem(`${DECLINED_PREFIX}${ownerKey}`) === null
  );
}

export function declinePinOffer(ownerKey: string): void {
  localStorage.removeItem(ARM_KEY);
  localStorage.setItem(`${DECLINED_PREFIX}${ownerKey}`, "1");
}

export function resetPinOffer(ownerKey: string | null): void {
  localStorage.removeItem(ARM_KEY);
  if (ownerKey !== null) {
    localStorage.removeItem(`${DECLINED_PREFIX}${ownerKey}`);
  }
}

export async function wipeVault(ownerKey: string | null): Promise<void> {
  await forgetSecret();
  resetPinOffer(ownerKey);
}

export function useVault() {
  const session = useSession();
  const queryClient = useQueryClient();
  const ownerKey = ownerKeyOf(session.user.tenant_id, session.user.id);
  const query = useQuery({
    queryKey: vaultQueryKey(ownerKey),
    queryFn: () => vaultStatus(ownerKey),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: vaultQueryKey(ownerKey) });
  }, [queryClient, ownerKey]);

  return { ownerKey, status: query.data ?? EMPTY_STATUS, loading: query.isPending, refresh };
}
